import { ComponentType } from "react";
import { Box, Text } from "@chakra-ui/react";
import { UIBlock, BaseBlockProps } from "./types";
import { TextBlock } from "./TextBlock";
import { CodeBlock } from "./CodeBlock";
import { CardBlock } from "./CardBlock";
import { TableBlock } from "./TableBlock";
import { ListBlock } from "./ListBlock";
import { ArtifactBlock } from "./ArtifactBlock";
import { DividerBlock } from "./DividerBlock";
import {
  ToolConfirmationBlock,
  setToolConfirmationBlockRenderer,
} from "./ToolConfirmationBlock";
import { StatusBlock } from "./StatusBlock";
import { KeyValueBlock } from "./KeyValueBlock";
import { LinkBlock } from "./LinkBlock";
import { ImageBlock } from "./ImageBlock";
import { AccordionBlock, setBlockRenderer } from "./AccordionBlock";

const PlaceholderBlock = ({ block }: BaseBlockProps) => (
  <Box p="sm" bg="neutral.50" borderRadius="component.md">
    <Text fontSize="xs" color="text.body.tertiary">
      Block type: {block.type}
    </Text>
  </Box>
);

const BLOCK_COMPONENTS: Record<string, ComponentType<BaseBlockProps>> = {
  text: TextBlock,
  markdown: TextBlock, // markdown is same as text (uses ReactMarkdown)
  code: CodeBlock,
  card: CardBlock,
  table: TableBlock,
  list: ListBlock,
  key_value: KeyValueBlock,
  status: StatusBlock,
  link: LinkBlock,
  divider: DividerBlock,
  image: ImageBlock,
  accordion: AccordionBlock,
  artifact: ArtifactBlock,
  confirmation: ToolConfirmationBlock as ComponentType<BaseBlockProps>,
};

interface BlockRendererProps {
  block: UIBlock;
  sessionId?: string;
  executionStatus?: string;
  /** Other blocks to render as preview content (for confirmation blocks) */
  previewBlocks?: UIBlock[];
}

export const BlockRenderer = ({
  block,
  sessionId,
  executionStatus,
  previewBlocks,
}: BlockRendererProps) => {
  const Component = BLOCK_COMPONENTS[block.type];

  if (!Component) {
    console.warn(`Unknown block type: ${block.type}`);
    return null;
  }

  // For confirmation blocks, pass additional props
  if (block.type === "confirmation") {
    const content = block.content as {
      execution_id?: string;
      message?: string;
      tool_label?: string;
    };
    return (
      <ToolConfirmationBlock
        block={block}
        sessionId={sessionId}
        executionId={content?.execution_id}
        status={executionStatus as any}
        confirmationMessage={content?.message}
        previewBlocks={previewBlocks}
      />
    );
  }

  return <Component block={block} />;
};

// Set the BlockRenderer for AccordionBlock and ToolConfirmationBlock to enable nested rendering
setBlockRenderer(BlockRenderer);
setToolConfirmationBlockRenderer(BlockRenderer);

export type { UIBlock, BaseBlockProps };
